import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SignAtpDialogComponent } from '../../sign-atp-dialog/sign-atp-dialog.component';
import { AllbidsDialogComponent } from '../../allbids-dialog/allbids-dialog.component';
import { ProjectSpecialRequestDialogComponent } from '../../project-special-request-dialog/project-special-request-dialog.component';
import { getStoredUser } from '../../helpers/user-storage.util';
import { SiteService } from '../../services/site.service';
import { ProjectService } from '../../services/project.service';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ModalService } from '../../services/modal.service';
import { AppService } from '../../services/app.service';
import { Router } from '@angular/router';
import { AcceptOfferedProposalDialogComponent } from '../../accept-offered-proposal-dialog/accept-offered-proposal-dialog.component';

@Component({
  selector: 'project-overview-header',
  standalone: true,
  imports: [
    CommonModule,
    SignAtpDialogComponent,
    AllbidsDialogComponent,
    ProjectSpecialRequestDialogComponent,
    AcceptOfferedProposalDialogComponent,
  ],
  templateUrl: './project-overview-header.component.html',
  styleUrl: './project-overview-header.component.scss',
})
export class ProjectOverviewHeaderComponent {
  @Input() expanded: boolean = false;
  @Input() data: any = '';
  @Input() type: string = '';
  @Input() id: number = 0;
  @Input() status: string = '';
  @Input() site: number = 0;
  @Input() address: any = { streetAddress: '', cityStateZip: '' };
  @Input() acreage: number = 0;
  @Input() landUse: string = '';
  @Input() tableA: string = '';
  @Input() item20OrItem21: string = '';
  @Input() dueDate: string = '';
  @Input() documents: any = '';
  @Input() turnTime: number = 0;
  @Input() bidAmount: number = 0;
  @Input() reviewStatus: string = '';
  @Input() atpDocumentPath: SafeResourceUrl = '';
  @Input() showStatus: boolean = true;
  @Input() showSite: boolean = true;
  @Input() showAddress: boolean = true;
  @Input() showAcreage: boolean = true;
  @Input() showLandUse: boolean = true;
  @Input() showSiteType: boolean = true;
  @Input() showDueDate: boolean = true;
  @Input() showActions: boolean = true;
  @Input() numberOfBids: number = 0;
  @Input() enableSubmitAllBids: boolean = false;
  @Input() enableAcceptAllOffers: boolean = false;
  @Input() showAtp: boolean = false;

  @Output() toggled = new EventEmitter<boolean>();

  openState: boolean = this.expanded;
  serviceInfoToggle: boolean = false;
  signAtpActive: boolean = false;
  allBidsActive: boolean = false;
  acceptAllOffersActive: boolean = false;
  editToggle: boolean = false;
  atpDocument: any;
  childModalHeaderTitle: string = 'ATP Signed & Project Active';
  childModalBody: string =
    'You have signed the ATP and the project can be viewed from the Active tab.';
  user = getStoredUser();
  specialRequestToggle: boolean = false;
  redirectUrl: string = '';

  constructor(
    private siteService: SiteService,
    private projectService: ProjectService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private modalService: ModalService,
    private appService: AppService,
    public router: Router
  ) {}

  ngOnInit() {
    this.openState = this.expanded;
    this.appService.redirectUrl.subscribe((url) => (this.redirectUrl = url));
  }

  goBack() {
    if (this.redirectUrl?.length > 0 || window.history.length <= 1) {
      this.router.navigate(['invitations']);
      this.appService.setRedirectUrl('');
    } else {
      window.history.back();
    }
  }

  toggleSubmitAllBidsModal() {
    this.allBidsActive = !this.allBidsActive;
  }

  toggleAtpAcceptedModal() {
    // project id can be null because the schedule site visit button
    // is not visible from project overview
    this.modalService.toggleOpenSignedAtpModal(null, false);
  }

  toggleSpecialRequestModal() {
    this.specialRequestToggle = !this.specialRequestToggle;
  }

  toggleAcceptAllOfferedProposalModal() {
    this.acceptAllOffersActive = !this.acceptAllOffersActive;
  }

  generateATP() {
    this.toastr.info(
      'Please wait a moment while we generate your ATP document. This process may take a few seconds.'
    );
    let atpDocumentResponse: any = null;
    this.siteService
      .generateAtp(this.data, this.user.id ?? '')
      .subscribe((response) => {
        if (response) {
          atpDocumentResponse = response;
          if (
            atpDocumentResponse.status === 'SUCCESS' &&
            atpDocumentResponse.data
          ) {
            this.atpDocument = atpDocumentResponse.data;
            if (this.atpDocument.documentPath) {
              this.atpDocumentPath =
                this.sanitizer.bypassSecurityTrustResourceUrl(
                  this.atpDocument.documentPath
                );
              this.toggleATPModal();
            } else {
              this.toastr.error(
                'There was an issue with genereating your ATP document. Please try again.'
              );
            }
          } else {
            this.toastr.error(atpDocumentResponse.message);
          }
        } else {
          this.toastr.error(
            'There was an issue with genereating your ATP document. Please try again.'
          );
        }
      });
  }

  signATP(event: { fullName: string }) {
    this.toastr.info('Accepting your signed ATP...');
    this.siteService
      .signAtp(
        {
          projectDetail: this.data,
          atpDocument: this.atpDocument,
          atpSignedBy: event.fullName,
        },
        this.user.id ?? ''
      )
      .subscribe((response) => {
        const updatedProjectDetail = response.data;
        this.projectService.updateProjectDataSubjectWithProjectDetail(
          updatedProjectDetail
        );
        this.data = updatedProjectDetail;
        this.determineIfButtonsShouldDisplay();
        this.toggleATPModal();
        this.toggleAtpAcceptedModal();
      });
  }

  toggleATPModal() {
    this.signAtpActive = !this.signAtpActive;
  }

  declineATP(event: {
    declineReason: string;
    declineOtherReason: string;
    declineComments: string;
  }) {
    const updatedSiteDetails = this.data.sites.map((siteDetail: any) => {
      const updatedProposal = {
        ...siteDetail.proposal,
        declineReason: event.declineReason,
        declineOtherReason: event.declineOtherReason,
        declineComments: event.declineComments,
      };
      return { ...siteDetail, proposal: updatedProposal };
    });
    this.siteService
      .cancel(this.user.id ?? '', { ...this.data, sites: updatedSiteDetails })
      .subscribe((response: any) => {
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.message) {
            this.toastr.error(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        } else {
          const updatedProjectDetail = response.data;
          if (updatedProjectDetail) {
            this.projectService.updateProjectDataSubjectWithProjectDetail(
              updatedProjectDetail
            );
            this.data = updatedProjectDetail;
            this.determineIfButtonsShouldDisplay();
            this.modalService.toggleOpenDeclineSuccessModal({}, false, true);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      });
  }

  private determineIfButtonsShouldDisplay() {
    this.showAtp = this.data?.sites?.some(
      (site: { proposal: { status: string } }) =>
        site.proposal?.status === 'BID_WON'
    );
    this.numberOfBids = this.data?.sites?.filter(
      (siteDetail: any) =>
        siteDetail.proposal.status === 'INVITATION' &&
        siteDetail.proposal.bid > 0 &&
        siteDetail.proposal.proposedTurnTime > 0
    ).length;
    this.enableSubmitAllBids = this.numberOfBids > 0;
    this.enableAcceptAllOffers = this.data?.sites?.some(
      (site: { proposal: { status: string } }) =>
        site.proposal?.status === 'OFFERED'
    );
  }
}
